import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { bookmarksApi } from '../../api/bookmarks';
import { writerFacingRoomName } from '../../utils/roomChat';
import './Bookmarks.css';
import ConfirmModal from '../Common/ConfirmModal';
import { useConfirm } from '../Common/useConfirm';

const MENU_MARGIN = 8;
const MENU_GAP = 4;
const MENU_MIN_WIDTH = 180;

/**
 * Viewport position for the bookmark ⋯ menu.
 * `buttonRect` is getBoundingClientRect() (viewport pixels). The menu is
 * portaled to document.body, so top/left are viewport pixels too — the same
 * space at any panel width, scroll, or zoom. Flip above the button when
 * there is more room there, and clamp so the menu stays inside the viewport.
 */
export function placeBookmarkMenu(buttonRect, menuSize = {}, viewport = {}, margin = MENU_MARGIN) {
  const viewW = Number(viewport.width) || 0;
  const viewH = Number(viewport.height) || 0;
  const maxWidth = viewW > margin * 2 ? viewW - margin * 2 : MENU_MIN_WIDTH;
  let width = Math.max(Number(menuSize.width) || 0, MENU_MIN_WIDTH);
  if (width > maxWidth) width = maxWidth;

  const natural = Math.max(0, Number(menuSize.height) || 0);
  const spaceBelow = Math.max(0, viewH - buttonRect.bottom - margin - MENU_GAP);
  const spaceAbove = Math.max(0, buttonRect.top - margin - MENU_GAP);
  const openBelow = natural === 0 || spaceBelow >= natural || spaceBelow >= spaceAbove;
  const room = openBelow ? spaceBelow : spaceAbove;
  const height = natural === 0 ? 0 : Math.min(natural, room);
  const maxHeight = natural > 0 && room > 0 && natural > room ? room : null;

  let left = buttonRect.right - width;
  const minLeft = margin;
  const maxLeft = Math.max(minLeft, viewW - width - margin);
  if (left > maxLeft) left = maxLeft;
  if (left < minLeft) left = minLeft;

  const used = height || natural;
  let top = openBelow
    ? buttonRect.bottom + MENU_GAP
    : buttonRect.top - MENU_GAP - used;
  if (top < margin) top = margin;
  if (used && top + used > viewH - margin) {
    top = Math.max(margin, viewH - margin - used);
  }

  return { top, left, width, maxHeight };
}

const CATEGORIES = [
  { value: 'character', label: 'Characters', color: '#3B82F6' },
  { value: 'plot', label: 'Plot', color: '#10B981' },
  { value: 'setting', label: 'Setting', color: '#8B5CF6' },
  { value: 'theme', label: 'Themes', color: '#F59E0B' }
];

/**
 * BookmarkListItem - Expandable bookmark item for the panel
 */
function BookmarkListItem({
  bookmark,
  onDelete,
  onCategoryChange,
  onTitleChange,
  folders = [],
  currentFolderId = null,
  onMoveToFolder,
  onReorder,
  canMoveUp = false,
  canMoveDown = false,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(bookmark.user_title || '');
  const [showMenu, setShowMenu] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const { confirm, modalProps } = useConfirm();
  const titleInputRef = useRef(null);
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const destinationFolders = folders.filter((folder) => folder.id !== currentFolderId);
  const showMoveToFolder = destinationFolders.length > 0 || Boolean(currentFolderId);

  const categoryInfo = CATEGORIES.find(c => c.value === bookmark.category);
  const roomLabel = writerFacingRoomName(bookmark.session_name, bookmark.room_key, bookmark.session_type);
  const typeIcon = bookmark.bookmark_type === 'synthesis' ? '📝' : '💬';
  const typeLabel = bookmark.bookmark_type === 'synthesis' ? 'Synthesis' : 'Message';

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  const closeMenu = () => {
    setShowMenu(false);
    menuButtonRef.current?.focus();
  };

  useLayoutEffect(() => {
    if (!showMenu) return undefined;
    const place = () => {
      const rect = menuButtonRef.current?.getBoundingClientRect();
      const menu = menuRef.current;
      if (!rect) return;
      setMenuPos(placeBookmarkMenu(
        rect,
        { width: menu?.offsetWidth || MENU_MIN_WIDTH, height: menu?.offsetHeight || 0 },
        { width: window.innerWidth, height: window.innerHeight },
      ));
    };
    place();
    const first = menuRef.current?.querySelector('[role="menuitem"]:not(:disabled)');
    first?.focus();
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setShowMenu(false);
      menuButtonRef.current?.focus();
    };
    const onPointer = (event) => {
      const target = event.target;
      if (menuRef.current?.contains(target) || menuButtonRef.current?.contains(target)) return;
      setShowMenu(false);
      menuButtonRef.current?.focus();
    };
    const onScroll = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      place();
    };
    window.addEventListener('resize', place);
    window.addEventListener('scroll', onScroll, true);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [showMenu]);

  const handleDelete = async (event) => {
    event?.stopPropagation?.();
    setShowMenu(false);
    const ok = await confirm({ title: 'Delete bookmark?', message: 'Delete this bookmark?', confirmLabel: 'Delete', destructive: true });
    if (!ok) return;
    onDelete?.(bookmark.id);
  };

  const handleCategoryChange = async (e) => {
    e.stopPropagation();
    const newCategory = e.target.value || null;
    try {
      await bookmarksApi.updateCategory(bookmark.id, newCategory);
      onCategoryChange?.(bookmark.id, newCategory);
    } catch (err) {
      alert('Failed to update category: ' + err.message);
    }
  };

  const handleStartEditTitle = (e) => {
    e.stopPropagation();
    setEditedTitle(bookmark.user_title || '');
    setIsEditingTitle(true);
  };

  const handleSaveTitle = async () => {
    const trimmedTitle = editedTitle.trim();
    if (!trimmedTitle) {
      setEditedTitle(bookmark.user_title || '');
      setIsEditingTitle(false);
      return;
    }

    if (trimmedTitle === bookmark.user_title) {
      setIsEditingTitle(false);
      return;
    }

    try {
      await bookmarksApi.updateTitle(bookmark.id, trimmedTitle);
      onTitleChange?.(bookmark.id, trimmedTitle);
      setIsEditingTitle(false);
    } catch (err) {
      alert('Failed to update title: ' + err.message);
    }
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveTitle();
    } else if (e.key === 'Escape') {
      setEditedTitle(bookmark.user_title || '');
      setIsEditingTitle(false);
    }
  };

  const handleMenuToggle = (e) => {
    e.stopPropagation();
    if (showMenu) {
      setShowMenu(false);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPos(placeBookmarkMenu(
      rect,
      { width: MENU_MIN_WIDTH, height: 0 },
      { width: window.innerWidth, height: window.innerHeight },
    ));
    setShowMenu(true);
  };

  const handleMenuKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closeMenu();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = [...(menuRef.current?.querySelectorAll('[role="menuitem"]:not(:disabled)') || [])];
    if (items.length === 0) return;
    e.preventDefault();
    const index = items.indexOf(document.activeElement);
    const next = e.key === 'ArrowDown'
      ? items[(index + 1) % items.length]
      : items[(index - 1 + items.length) % items.length];
    next?.focus();
  };

  const handleMoveToFolder = (folderId) => {
    onMoveToFolder?.(bookmark.id, folderId);
    closeMenu();
  };

  const handleReorder = (direction) => {
    onReorder?.(bookmark.id, direction);
    closeMenu();
  };

  const runMenuAction = (action) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    action();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  return (
    <div
      className={`bookmark-list-item ${isExpanded ? 'bookmark-list-item--expanded' : ''} ${showMenu ? 'bookmark-list-item--menu-open' : ''}`}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      {/* Collapsed Header - Always visible */}
      <div className="bookmark-list-item__header">
        <span className="bookmark-list-item__icon" title={typeLabel}>
          {typeIcon}
        </span>
        <span className="bookmark-list-item__title">
          {bookmark.user_title || 'Untitled'}
        </span>
        {roomLabel && (
          <span className="bookmark-list-item__date">{roomLabel}</span>
        )}
        {categoryInfo && (
          <span
            className="bookmark-list-item__category-dot"
            style={{ backgroundColor: categoryInfo.color }}
            title={categoryInfo.label}
          />
        )}
        <div className="bookmark-folder-menu-container" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            ref={menuButtonRef}
            className="bookmark-folder-menu-button"
            onClick={handleMenuToggle}
            aria-label="Bookmark options"
            aria-haspopup="menu"
            aria-expanded={showMenu}
          >
            ⋯
          </button>
          {showMenu && menuPos && createPortal(
            <div
              ref={menuRef}
              className="bookmark-folder-menu bookmark-folder-menu--portal"
              style={{
                position: 'fixed',
                top: menuPos.top,
                left: menuPos.left,
                width: menuPos.width,
                maxHeight: menuPos.maxHeight || undefined,
                zIndex: 2000,
              }}
              role="menu"
              aria-label="Bookmark options"
              onKeyDown={handleMenuKeyDown}
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                role="menuitem"
                className="bookmark-folder-menu-item"
                onClick={runMenuAction(() => handleReorder('up'))}
                disabled={!canMoveUp}
              >
                Move up
              </button>
              <button
                type="button"
                role="menuitem"
                className="bookmark-folder-menu-item"
                onClick={runMenuAction(() => handleReorder('down'))}
                disabled={!canMoveDown}
              >
                Move down
              </button>
              {showMoveToFolder && (
                <div className="bookmark-folder-menu-label">Move to folder</div>
              )}
              {destinationFolders.map((folder) => (
                <button
                  type="button"
                  role="menuitem"
                  key={folder.id}
                  className="bookmark-folder-menu-item"
                  onClick={runMenuAction(() => handleMoveToFolder(folder.id))}
                >
                  {folder.name}
                </button>
              ))}
              {currentFolderId && (
                <button
                  type="button"
                  role="menuitem"
                  className="bookmark-folder-menu-item"
                  onClick={runMenuAction(() => handleMoveToFolder(null))}
                >
                  No folder
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                className="bookmark-folder-menu-item bookmark-folder-menu-item--danger"
                onClick={runMenuAction(handleDelete)}
              >
                Delete
              </button>
            </div>,
            document.body,
          )}
        </div>
        <span className={`bookmark-list-item__chevron ${isExpanded ? 'bookmark-list-item__chevron--open' : ''}`}>
          ▸
        </span>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="bookmark-list-item__content" onClick={(e) => e.stopPropagation()}>
          {/* Editable Title */}
          <div className="bookmark-list-item__title-row">
            {isEditingTitle ? (
              <input
                ref={titleInputRef}
                type="text"
                className="bookmark-list-item__title-input"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                onKeyDown={handleTitleKeyDown}
                onBlur={handleSaveTitle}
                placeholder="Enter title..."
              />
            ) : (
              <>
                <span className="bookmark-list-item__title-text">
                  {bookmark.user_title || 'Untitled'}
                </span>
                <button
                  className="bookmark-list-item__edit-btn"
                  onClick={handleStartEditTitle}
                  title="Edit title"
                >
                  ✏️
                </button>
              </>
            )}
          </div>

          <div className="bookmark-list-item__text">
            {bookmark.content}
          </div>

          <div className="bookmark-list-item__footer">
            <select
              className="bookmark-list-item__category-select"
              value={bookmark.category || ''}
              onChange={handleCategoryChange}
            >
              <option value="">No category</option>
              {CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>

            <span className="bookmark-list-item__date">
              {formatDate(bookmark.created_at)}
            </span>

            <button
              className="bookmark-list-item__delete"
              onClick={handleDelete}
              title="Delete bookmark"
            >
              🗑️
            </button>
          </div>
        </div>
      )}
      <ConfirmModal {...modalProps} />
    </div>
  );
}

export default BookmarkListItem;
